import React, {useCallback, useMemo, useState, useEffect, useContext, useId} from "react";
import {openUrl} from "utils/url-helper";
import {captureClickEvent} from "utils/hotspot-helper";
import { AttentionAnimation } from 'utils/AttentionAnimationProvider';

import style from "./style.module.scss";
import {HotSpotType, AdUnitEvent, JourneyTriggerInteractiveEvent, MARKER, EFFECT_PREFIX} from "utils/constants";
import {useConfig} from "utils/ConfigProvider";
import classnames from "classnames";
import {useHotspotContainerDimension} from 'hooks/useHotspotContainerDimension';
import {HotspotRenderer} from "modules/hotspot-manager/hotspotRenderer";
import {useHotspotInteractions} from "hooks/useHotspotInteractions";

const noOp = () => {
};

/**
 * Responsible for rendering hotspots, and hotspots engagements
 * @param settings
 * @param onEvent
 * @param fireJourneyTrigger
 * @param setHotspotOverlay
 * @param hotspots
 * @param onHotspotClickAway,
 * @param showing
 * @returns {*}
 * @constructor
 */
function HotspotManager(
  {
    onEvent,
    fireJourneyTrigger,
    setHotspotOverlay,
    hotspots,
    allHotspots,
    onHotspotClickAway = noOp,
    onClickThrough,
    showing,
    isCardHotspot = false,
    isCardAndInVideoAndFinale = false,
    isInVideoOverlay = false,
    isFinaleOverlay = false,
    isDisplayHotspot = false,
    useHotspotAnimation = false,
    overlayID,
    setPixelFiringInProgress = noOp, // noop,
    hotspotProps,
    isStandaloneHotspots = false,
    // Used to just render the Hotspots, and the container will be rendered within Overlay (Example in Custom Survey) bringing all the hotspots in same layer
    renderWithoutContainer = false,
    containerDimensions: propContainerDimensions,
    hotspotContainer: propHotspotContainer,
    interactProps: propInteractProps,
    isSharedCard = false
  }
) {
  const instanceId = useId();
  const {environment} = useConfig();
  const {containerDimensions, setHotspotContainer, hotspotContainer} = useHotspotContainerDimension();

  const {
    attentionHovered, 
    setAttentionHovered, 
    attentionHoveredPreviously, 
    setAttentionHoveredPreviously, 
    attentionResetKey, 
    setAttentionResetKey
  } = useContext(AttentionAnimation);

  const [customMarkersLoaded, setCustomMarkersLoaded] = useState(0);
  const [allCustomMarkersLoaded, setAllCustomMarkersLoaded] = useState(false);

  const isCreativeStudioPreviewMode = useMemo(() => {
    return environment.isCreativeStudioPreviewMode instanceof Function ? environment.isCreativeStudioPreviewMode() : false;
  }, [environment]);

  const isInScreenShooterEmulation = useMemo(() => {
    return environment.isInScreenShooterEmulation instanceof Function ? environment.isInScreenShooterEmulation() : false;
  }, [environment]);

  const hotspotsToBeRendered = useMemo(() => {
    return allHotspots ? allHotspots : hotspots;
  }, [allHotspots, hotspots]);

  const isShowing = useCallback((hotspot) => {
    // When rendered without container, check for propHotspotContainer instead.
    if (renderWithoutContainer) {
      if (!propHotspotContainer) {
        return false;
      }
    } else {
      if (!hotspotContainer) {
        return false;
      }
    }

    if (isCreativeStudioPreviewMode) {
      return true;
    }

    if (allHotspots) {
      return Boolean(hotspots.find(({id}) => hotspot.id === id));
    }

    return true;
  }, [allHotspots, hotspotContainer, hotspots, isCreativeStudioPreviewMode, propHotspotContainer, renderWithoutContainer]);

  const isStandaloneHotspotShowing = useCallback((index) => {
    if (!isCreativeStudioPreviewMode) {
      return true;
    }

    return hotspotProps?.standaloneSelectedHotspotIndex === index;
  }, [hotspotProps?.standaloneSelectedHotspotIndex, isCreativeStudioPreviewMode]);

  // NOTE: not sure why this isn't combined with isShowing, but lifting it from hotspotRenderer for attention animation
  const shouldHide = useCallback((hotspot, index) => {
    const standaloneHotspotShowing = isStandaloneHotspotShowing(index);

    // Hide Hotspot, when in preview mode, and hotspot panel is not open. - Refer OC-700
    if (isCreativeStudioPreviewMode && isStandaloneHotspots && !standaloneHotspotShowing) {
      return true
    }

    // OC-956 for cards inVideo and finale check hotspot visibility options (inVideo, finaleEnabled)
    if (isCardAndInVideoAndFinale) {
      if (!hotspot.inVideo && isInVideoOverlay) {
        return true
      }
      if (!hotspot.finaleEnabled && isFinaleOverlay) {
        return true
      }
    }
    return false
  }, [isCreativeStudioPreviewMode, isStandaloneHotspots, isCardAndInVideoAndFinale, isInVideoOverlay, isFinaleOverlay, isStandaloneHotspotShowing])

  const attentionAnimationMap = hotspotsToBeRendered.reduce((acc, hotspot, index) => {
    const hasEffect = hotspot.effect && hotspot.effect.effectType !== EFFECT_PREFIX.NONE;
    const isAttentionReady = hasEffect && hotspot.attentionAnimationEnabled && isShowing(hotspot) && !shouldHide(hotspot, index);
    const showAttentionAnimation = isAttentionReady && attentionHovered === null;

    let animationIndex = null;
    if (isAttentionReady) {
      animationIndex = acc._count;
      acc._count++
    } 

    acc[index] = {
      showAttentionAnimation,
      animationIndex
    }

    if (hotspot.marker === MARKER.CUSTOM) {
      acc._customMarkerCount++
    }

    return acc;
  }, {_count: 0, _customMarkerCount: 0})


  useEffect(() => {
    if (attentionAnimationMap._customMarkerCount === customMarkersLoaded) {
      setAllCustomMarkersLoaded(true);
    }
  }, [attentionAnimationMap._customMarkerCount, customMarkersLoaded]);

  const attentionCount = attentionAnimationMap?._count ?? 0;
  const hotspotIdSeq = hotspotsToBeRendered.map(({id, tempId}) => id || tempId).join("_");
  
  useEffect(() => {
    if (isCreativeStudioPreviewMode) {
      setAttentionHoveredPreviously(false)
      setAttentionResetKey(prevKey => prevKey + 1); 
    }
  },[attentionCount, hotspotIdSeq, setAttentionHoveredPreviously, setAttentionResetKey, isCreativeStudioPreviewMode])

  // Clean up attention hover on unmount
  useEffect(() => {
    return () => {
      setAttentionHovered(curr => (curr === instanceId ? null : curr)) 
    };
  }, [setAttentionHovered, instanceId]);

  const interactProps = useHotspotInteractions({
    canInteract: !!(!renderWithoutContainer && isCreativeStudioPreviewMode && (!isSharedCard || hotspotProps?.allowSharedCardEdit)),
    hotspotProps,
    selectedHotspots: hotspotProps?.selectedHotspots,
    onChangeDeltaOnly: isSharedCard,
  });

  const {
    canInteract,
    setHotspotInteractionContainer,
    openHotspot,
    isHotspotSelected,

    selectedHotspotSelector,
    hotspotPopperSelector,
    snappingHotspotSelector,
  } = useMemo(() => {
    if (renderWithoutContainer) {
      return propInteractProps || {};
    }
    return interactProps;
  }, [interactProps, propInteractProps, renderWithoutContainer])

  const setHotspotContainerRef = useCallback((ref) => {
    setHotspotContainer(ref);
    if (setHotspotInteractionContainer instanceof Function) {
      setHotspotInteractionContainer(ref);
    }
  }, [setHotspotContainer, setHotspotInteractionContainer]);

  const onHotspotClick = useCallback((hotspot, index, isSelected, shiftKey) => {
    if (isCreativeStudioPreviewMode) {
      if (canInteract && hotspotProps) {
        if (!isSelected) {
          openHotspot(hotspot, shiftKey);
        }
      }
    } else {
      const {id, inSessionTriggerDetails, hotspotType, linkToUrl, hasChildren, marker} = hotspot;
      const {startTime, endTime} = inSessionTriggerDetails?.elementTimeTriggers?.[hotspot.timeTriggerIndex] || {};
      const triggerElementId = inSessionTriggerDetails?.triggerElementId;
      const cardID = (isCardHotspot || isDisplayHotspot) ? overlayID : null;
      let currentHotspotType = '';
      if (HotSpotType[hotspotType] === HotSpotType.ENGAGEMENT_EXPERIENCE) {
        onHotspotClickAway();
        currentHotspotType = HotSpotType.ENGAGEMENT_EXPERIENCE;
        setHotspotOverlay(hotspot);
      } else {
        if (linkToUrl) {
          onHotspotClickAway();
          currentHotspotType = HotSpotType.DRIVE_TO_SITE;
          if (marker !== MARKER.EMBED) {
            openUrl(linkToUrl, '_blank');
          }
          setPixelFiringInProgress(true)
          onEvent(captureClickEvent(AdUnitEvent.HotSpotDriveToSiteClick, currentHotspotType, id, startTime, endTime, isDisplayHotspot, cardID, isSharedCard))
            .then(() => {
              setPixelFiringInProgress(false);
            }).catch(e => setPixelFiringInProgress(false));
        }
        fireJourneyTrigger(JourneyTriggerInteractiveEvent.interactiveHotspotClickThrough, triggerElementId, !linkToUrl, hasChildren);
        if (linkToUrl) {
          onClickThrough(null, null, true)
        }
      }
      if (HotSpotType[hotspotType] === HotSpotType.SENSITIVE_HEALTH_DATA) {
        currentHotspotType = HotSpotType.SENSITIVE_HEALTH_DATA;
      }
      onEvent(captureClickEvent(AdUnitEvent.HotSpotInteraction, currentHotspotType, id, startTime, endTime, isDisplayHotspot, cardID, isSharedCard));
      fireJourneyTrigger(JourneyTriggerInteractiveEvent.interactiveHotspotInteraction, triggerElementId, false, hasChildren);
    }
  }, [canInteract, hotspotProps, openHotspot, isCardHotspot, isDisplayHotspot, overlayID, onEvent, fireJourneyTrigger, onHotspotClickAway, setHotspotOverlay, isCreativeStudioPreviewMode, setPixelFiringInProgress, onClickThrough, isSharedCard]);

  const renderHotspots = useCallback(() => {
    return (hotspotsToBeRendered).map(
      (hotspot, index) => {
        return <HotspotRenderer
          key={hotspot.id || index}
          isCreativeStudioPreviewMode={isCreativeStudioPreviewMode}
          index={index}
          hotspot={hotspot}
          isCardAndInVideoAndFinale={isCardAndInVideoAndFinale}
          onHotspotClick={onHotspotClick}
          containerDimensions={propContainerDimensions ? propContainerDimensions : containerDimensions}
          isCardHotspot={isCardHotspot}
          useHotspotAnimation={useHotspotAnimation}
          isInVideoOverlay={isInVideoOverlay}
          isFinaleOverlay={isFinaleOverlay}
          isShowing={isShowing}
          isStandaloneHotspots={isStandaloneHotspots}
          isHotspotSelected={isHotspotSelected}
          selectedHotspotSelector={selectedHotspotSelector}
          hotspotPopperSelector={hotspotPopperSelector}
          snappingHotspotSelector={snappingHotspotSelector}
          shouldHide={shouldHide}

          showAttentionAnimation={allCustomMarkersLoaded ? attentionAnimationMap[index].showAttentionAnimation : false}
          animationIndex={attentionAnimationMap[index].animationIndex}
          onMouseOver={() => {
            setAttentionHovered(instanceId);
            setAttentionHoveredPreviously(true);
          }}
          onMouseOut={() => setAttentionHovered(null)}
          resetKey={attentionResetKey}
          delayInitialAttentionAnimation={attentionHoveredPreviously}
          delayInitialAttentionAnimationForInVideo={isInVideoOverlay}
          onImageLoaded={() => setCustomMarkersLoaded(prev => prev + 1)}
        />
      }
    );
  }, [propContainerDimensions, containerDimensions, hotspotPopperSelector, hotspotsToBeRendered, isCardAndInVideoAndFinale, isCardHotspot, 
      isCreativeStudioPreviewMode, isFinaleOverlay, isHotspotSelected, isInVideoOverlay, isShowing, shouldHide, 
      isStandaloneHotspots, onHotspotClick, selectedHotspotSelector, snappingHotspotSelector, useHotspotAnimation, allCustomMarkersLoaded, 
      attentionAnimationMap, attentionHoveredPreviously, attentionResetKey, setAttentionHovered, setAttentionHoveredPreviously, instanceId])

  if (!showing) {
    return <React.Fragment/>
  }

  if (hotspotsToBeRendered?.length) {
    return renderWithoutContainer ?
      renderHotspots() :
      <div
        className={classnames(style.hotspotContainer, {
          [style.fullContainer]: (isCardHotspot || isDisplayHotspot),
          [style.pointerEventsAll]: isCreativeStudioPreviewMode && !isInScreenShooterEmulation && !(hotspotProps?.standaloneSelectedHotspotIndex === -1)
        })}
        ref={setHotspotContainerRef}
      >
        {renderHotspots()}
      </div>;
  }

  return <React.Fragment/>
}

export default HotspotManager;
